// Active sessions (Platform Operations Console P2). Cross-tenant list of live
// sessions with an operator revoke (force sign-out). Revoke captures a reason and
// is audit-first server-side (action session.revoke). auth-service /platform/sessions.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Space, Typography, message } from 'antd';
import { ReloadOutlined, LogoutOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, useUrlTableState, useConfirmWithReason, SavedViews, ExportButton, type CsvColumn } from '../../components/console';
import { listSessions, revokeSession, type PlatformSession } from '../../services/sessionService';

const EXPORT_COLUMNS: CsvColumn<PlatformSession>[] = [
  { header: 'User', value: (s) => s.userEmail || s.userId },
  { header: 'Name', value: (s) => s.userName || '' },
  { header: 'Tenant', value: (s) => s.tenant || 'platform' },
  { header: 'Browser', key: 'browser' },
  { header: 'OS', key: 'os' },
  { header: 'Device', key: 'device' },
  { header: 'IP', key: 'ip' },
  { header: 'Location', key: 'location' },
  { header: 'MFA', value: (s) => (s.mfaVerified ? 'yes' : 'no') },
  { header: 'Last activity', value: (s) => (s.lastActivityAt ? new Date(s.lastActivityAt).toISOString() : '') },
  { header: 'Expires', value: (s) => (s.expiresAt ? new Date(s.expiresAt).toISOString() : '') },
];

const { Text } = Typography;

const fmt = (s?: string | null) => (s ? fmtDateTime(s) : '—');

// Relative "last seen" for the activity column.
const ago = (s?: string | null): string => {
  if (!s) return '—';
  const secs = Math.max(0, (Date.now() - new Date(s).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
};

export const SessionsPage: React.FC = () => {
  const url = useUrlTableState([]);
  const { confirm, dialog } = useConfirmWithReason();
  const [rows, setRows] = useState<PlatformSession[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pageSize = 50;

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await listSessions({ page: url.page, limit: pageSize });
      setRows(res.sessions || []);
      setTotal(res.pagination?.total ?? 0);
    } catch {
      if (initial) message.error('Could not load sessions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [url.page]);

  useEffect(() => { void load(true); }, [load]);

  const doRevoke = async (s: PlatformSession) => {
    const reason = await confirm({
      title: 'Revoke session?',
      message: (
        <>
          Force sign-out of <Text strong>{s.userEmail ?? s.userId}</Text>
          {s.device ? <> on <Text strong>{s.device}</Text></> : null}
          {s.tenant ? <> ({s.tenant})</> : null}. The user must sign in again.
        </>
      ),
      okText: 'Revoke',
      danger: true,
      reasonRequired: true,
      reasonLabel: 'Reason (audited)',
    });
    if (reason === null) return;
    try {
      await revokeSession(s.id, reason);
      message.success('Session revoked');
      await load(false);
    } catch {
      message.error('Could not revoke session');
    }
  };

  const columns: ColumnsType<PlatformSession> = useMemo(() => [
    {
      title: 'User',
      render: (_: unknown, s) => (
        <div>
          <div>{s.userEmail ?? s.userId}</div>
          {s.userName && <Text type="secondary" style={{ fontSize: 12 }}>{s.userName}</Text>}
        </div>
      ),
    },
    { title: 'Tenant', width: 150, render: (_: unknown, s) => s.tenant || <Text type="secondary">platform</Text> },
    {
      title: 'Device',
      render: (_: unknown, s) => (
        <Text type="secondary">{[s.browser, s.os, s.device].filter(Boolean).join(' · ') || '—'}</Text>
      ),
    },
    {
      title: 'IP / location', width: 200,
      render: (_: unknown, s) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.ip || '—'}</div>
          {s.location && <Text type="secondary" style={{ fontSize: 12 }}>{s.location}</Text>}
        </div>
      ),
    },
    {
      title: 'MFA', width: 80,
      render: (_: unknown, s) => <Tag color={s.mfaVerified ? 'success' : 'default'}>{s.mfaVerified ? 'yes' : 'no'}</Tag>,
    },
    {
      title: 'Last activity', width: 150,
      render: (_: unknown, s) => <span title={fmt(s.lastActivityAt)}>{ago(s.lastActivityAt)}</span>,
    },
    { title: 'Expires', width: 170, render: (_: unknown, s) => fmt(s.expiresAt) },
    {
      title: '', width: 110,
      render: (_: unknown, s) => (
        <Button size="small" danger icon={<LogoutOutlined />} onClick={() => void doRevoke(s)}>
          Revoke
        </Button>
      ),
    },
  ], []);

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Active sessions"
        subtitle="Live sessions across all tenants — revoke to force sign-out"
        actions={
          <Space wrap>
            <Text type="secondary">{total} active</Text>
            <SavedViews pageKey="sessions" />
            <ExportButton filename="active-sessions" rows={rows} columns={EXPORT_COLUMNS} />
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
              Refresh
            </Button>
          </Space>
        }
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: url.page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p) => url.setPage(p),
        }}
      />
      {dialog}
    </div>
  );
};

export default SessionsPage;
